import {
  ReflectionsResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import MenuBookIcon from '@assets/images/icons/menu-book.svg';
import EmptyIcon from '@assets/images/icons/wellbeing-journal-empty.svg';
import { ApiResponse } from '@interfaces/index';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { MotionImage, MotionText } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import { CoreLayout } from '@shared/templates';
import { useQuery } from '@tanstack/react-query';
import { formatDateToDateFullMonthYear } from '@utils/date.util';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styles from './styles.module.scss';

interface TruncatedTextProps {
  text: string;
  maxLength: number;
  className: string;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxLength, className }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text.length <= maxLength) {
    return (
      <Typography
        className={className}
        component="blockquote"
        sx={{
          fontSize: {
            xs: '14px',
            sm: '16px',
            md: '18px',
          },
        }}
      >
        {text || 'No reflection text'}
      </Typography>
    );
  }

  const truncatedText = text.substring(0, maxLength);
  const remainingText = text.substring(maxLength);

  return (
    <Typography
      className={className}
      component="blockquote"
      sx={{
        fontSize: {
          xs: '14px',
          sm: '16px',
          md: '18px',
        },
      }}
    >
      {truncatedText}
      {!isExpanded && '...'}
      {isExpanded && remainingText}
      <Box
        component="span"
        sx={{
          color: '#019df2',
          cursor: 'pointer',
          fontWeight: 500,
          marginLeft: '8px',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-label={isExpanded ? 'Show less text' : 'Show more text'}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </Box>
    </Typography>
  );
};

const LoadingSkeleton = ({ isMobile }: { isMobile: boolean }) => (
  <Timeline position={isMobile ? 'right' : 'alternate'}>
    {[1, 2, 3, 4, 5, 6].map((item) => (
      <TimelineItem key={`skeleton-${item}`}>
        {!isMobile && (
          <TimelineOppositeContent sx={{ m: 'auto 0' }}>
            <Skeleton
              variant="text"
              width={120}
              height={24}
              sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
            />
          </TimelineOppositeContent>
        )}
        <TimelineSeparator>
          <TimelineConnector />
          <TimelineDot>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
            />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent sx={{ py: '12px', px: 2 }}>
          {isMobile && (
            <Skeleton
              variant="text"
              width={120}
              height={20}
              sx={{
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                marginBottom: '8px',
              }}
            />
          )}
          <Skeleton
            variant="text"
            width="100%"
            height={200}
            sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
          />
        </TimelineContent>
      </TimelineItem>
    ))}
  </Timeline>
);

export const ReflectionsPage: React.FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [reflections, setReflections] = useState<ReflectionsResponse[] | []>([]);

  const fetchAllUserReflections = async (): Promise<ApiResponse<ReflectionsResponse[]>> =>
    assessmentQuestionnaireApiHandler.getReflections();

  const {
    data: reflectionsData,
    isLoading: reflectionsLoading,
    error: reflectionsErrors,
  } = useQuery({
    queryKey: ['fetchAllUserReflections'],
    queryFn: fetchAllUserReflections,
    enabled: true,
  });

  useEffect(() => {
    if (reflectionsData?.success) {
      const sortedReflections = [...reflectionsData.responseObject].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setReflections(sortedReflections);
    } else {
      setReflections([]);
    }
  }, [reflectionsData]);

  return (
    <CoreLayout title="Reflections Page" navigationType="default">
      <div className="container" role="main">
        <div className={styles.reflectionsContainer}>
          <header className={styles.title}>
            <h1 id="page-title">Journals</h1>
            <SystemButton
              onClick={() => history.push('/journal')}
              className={styles.createReflectionBtn}
              aria-label="Create new reflection"
            >
              Create Journal
            </SystemButton>
          </header>

          {reflections?.length > 0 && !reflectionsErrors && (
            <MotionText
              className={styles.descriptionTextForJournals}
              as="text"
              text="This is your private space to write down thoughts and help you process emotions and gain clarity."
            />
          )}

          {reflectionsLoading ? (
            <Box aria-label="Loading reflections" aria-live="polite">
              <LoadingSkeleton isMobile={isMobile} />
            </Box>
          ) : reflections?.length === 0 || reflectionsErrors ? (
            <div className={styles.emptyJournal}>
              <MotionImage
                src={EmptyIcon}
                alt=""
                aria-hidden="true"
                className={styles.emptyIcon}
                shape="rectangle"
                skeletonHeight="50px"
                skeletonWidth="50px"
              />
              <MotionText as="h4" text="What's on your mind today?" />
              <MotionText
                as="text"
                text="This is your private space to write down thoughts and help you process emotions and gain clarity."
              />
            </div>
          ) : (
            <Timeline
              position={isMobile ? 'right' : 'alternate'}
              aria-labelledby="page-title"
              sx={{ padding: 0 }}
            >
              {reflections?.map((refl, index) => (
                <TimelineItem
                  key={index}
                  tabIndex={0}
                  aria-label={`Reflection ${index + 1} from ${
                    refl?.createdAt
                      ? formatDateToDateFullMonthYear(refl?.createdAt)
                      : 'unknown date'
                  }`}
                >
                  {!isMobile && (
                    <TimelineOppositeContent
                      sx={{
                        m: 'auto 0',
                        fontSize: {
                          xs: '14px',
                          sm: '16px',
                          md: '18px',
                        },
                      }}
                      align="right"
                      variant="body2"
                      color="text.secondary"
                    >
                      {refl?.createdAt ? formatDateToDateFullMonthYear(refl?.createdAt) : 'No date'}
                    </TimelineOppositeContent>
                  )}
                  <TimelineSeparator>
                    <TimelineConnector sx={{ bgcolor: '#3fb9fc' }} />
                    <TimelineDot
                      variant="outlined"
                      sx={{ border: '2px solid #019df2' }}
                      aria-hidden="true"
                    >
                      <MotionImage
                        src={MenuBookIcon}
                        alt=""
                        aria-hidden="true"
                        className={styles.menuBookIcon}
                        shape="rectangle"
                        skeletonHeight="25px"
                        skeletonWidth="25px"
                      />
                    </TimelineDot>
                    <TimelineConnector sx={{ bgcolor: '#3fb9fc' }} />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    {isMobile && (
                      <Typography
                        className={styles.mobileDate}
                        sx={{
                          marginBottom: '8px',
                        }}
                      >
                        {refl?.createdAt
                          ? formatDateToDateFullMonthYear(refl?.createdAt)
                          : 'No date'}
                      </Typography>
                    )}
                    {/* TODO: ADD TITLE IN THE FUTURE */}
                    {/* <Typography variant="h6" component="h2">
                      {`Reflection ${index + 1}`}
                    </Typography> */}
                    <TruncatedText
                      text={refl?.reflection || ''}
                      maxLength={300}
                      className={styles.reflectionText}
                    />
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </div>
      </div>
    </CoreLayout>
  );
};
